/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
// @ts-nocheck
import { dayjs, isBusinessDay, getDateFormatted, ET } from 'util/shared/date';
import { useTheme } from '@mui/material/styles';

import { getEquitiesBySyms, sigHighLow } from 'util/hiro';
import { PriceLineKeys } from 'types/hiro';
import { REVERSE_FIELD_NAME_MAPPING } from 'config/equityhub';
import { useSetRecoilState } from 'recoil';
import {
  useQuery,
  UseQueryOptions,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';

import { DefaultService } from 'gen/stream-api/requests/services.gen';
import { CANDLE_IDX } from 'types/impliedVol';
import { spotgammaDataState } from 'states';
import { getQueryDate } from 'util/shared/date';

export interface HiroKeyLevelsResult {
  begDate: Date;
  endDate: Date;
  keyLevel: PriceLineKey;
  price: number;
  color: string;
}

export const useHiroKeyLevels = <
  TData = HiroKeyLevelsResult[],
  TError = unknown,
>(
  {
    sym,
    beginDate,
    endDate,
  }: {
    sym: string;
    beginDate: dayjs.Dayjs;
    endDate: dayjs.Dayjs;
  },
  options?: Omit<UseQueryOptions<TData, TError>, 'queryKey' | 'queryFn'>,
) => {
  const queryClient = useQueryClient();
  const theme = useTheme();

  const setHiroKeyLevels = useSetRecoilState(spotgammaDataState);

  return useQuery<TData, TError>({
    placeholderData: keepPreviousData,
    queryKey: [
      'useHiroKeyLevels',
      {
        sym,
        beginDate: beginDate.valueOf(),
        endDate: endDate.valueOf(),
      },
    ],
    queryFn: async (): Promise<TData> => {
      const dates: dayjs.Dayjs[] = [];
      for (
        let d = beginDate.tz(ET).startOf('day');
        d < endDate;
        d = d.add(1, 'day')
      ) {
        if (isBusinessDay(d)) {
          dates.push(d);
        }
      }

      const now = dayjs();

      const [openings, equitiesBySyms] = await Promise.all([
        Promise.all(
          [...new Set([sym, 'SPX'])].flatMap((sym) =>
            dates
              .map((date) => {
                const marketOpen = date
                  .tz(ET)
                  .hour(9)
                  .minute(30)
                  .second(0)
                  .millisecond(0);
                if (marketOpen > now) {
                  return;
                }
                return queryClient.fetchQuery({
                  queryKey: [
                    'useHiroKeyLevelsOpeningPrice',
                    { sym, date: date.valueOf() },
                  ],
                  staleTime: 60 * 60 * 1_000,
                  queryFn: async () =>
                    DefaultService.getPrices({
                      symbol: sym,
                      field: 'price',
                      timeGte: marketOpen.format(),
                      candleDurationMs: 5_000,
                      limit: 11,
                    }).then((rsp) => {
                      const price = rsp.candles
                        .map((c) => c[CANDLE_IDX.OPEN])
                        .find(Boolean);
                      return (
                        price != null && {
                          sym,
                          date,
                          price,
                        }
                      );
                    }),
                });
              })
              .filter(Boolean),
          ),
        ),
        Promise.all(
          dates
            .map((date) =>
              queryClient.fetchQuery({
                queryKey: [
                  'useHiroKeyLevelsEquitiesBySyms',
                  { sym, date: date.valueOf() },
                ],
                staleTime: 60 * 60 * 1_000,
                queryFn: async () => {
                  const rsp = await getEquitiesBySyms(date, sym);
                  if (getQueryDate(true).isSame(date, 'day')) {
                    setHiroKeyLevels(rsp);
                  }
                  return {
                    equitiesBySym: rsp,
                    date,
                  };
                },
              }),
            )
            .filter(Boolean),
        ),
      ]);

      const openFromDateFromSym = {};
      for (const { sym, date, price } of openings) {
        const openFromDate = (openFromDateFromSym[sym] =
          openFromDateFromSym[sym] ?? {});
        openFromDate[date.valueOf()] = price;
      }

      const result = [];

      const equitiesBySymFromDate = new Map(
        equitiesBySyms.map(({ equitiesBySym, date }) => [
          date.valueOf(),
          equitiesBySym,
        ]),
      );

      for (const date of dates) {
        const equitiesBySym = equitiesBySymFromDate.get(date.valueOf());
        for (const keyLevel of PriceLineKeys) {
          let price;
          switch (keyLevel) {
            case 'SG Implied 1d Move High':
            case 'SG Implied 1d Move Low': {
              const highLow = sigHighLow(
                equitiesBySym,
                [
                  {
                    sym,
                    date: getDateFormatted(date),
                    price: openFromDateFromSym[sym]?.[date.valueOf()],
                  },
                  {
                    sym: 'SPX',
                    date: getDateFormatted(date),
                    price: openFromDateFromSym['SPX']?.[date.valueOf()],
                  },
                ],
                getDateFormatted(date.tz(ET)),
              );
              price =
                keyLevel === 'SG Implied 1d Move Low'
                  ? highLow?.sig_low
                  : highLow?.sig_high;
              break;
            }
            default:
              price = equitiesBySym?.[REVERSE_FIELD_NAME_MAPPING[keyLevel]] as
                | number
                | undefined;
          }
          if (price != null) {
            result.push({
              begDate: date.toDate(),
              endDate: date.add(1, 'day').startOf('day').toDate(),
              keyLevel,
              price,
              color: theme?.palette.hiro.priceLines[
                REVERSE_FIELD_NAME_MAPPING[keyLevel]
              ] as string,
            });
          }
        }
      }

      return result;
    },
    ...options,
  });
};
